import React from 'react';
import { FeedFormData, IncidentFormData } from '../types';
import { FEED_TYPES, FEED_TOLERANCES } from '../../../../dtos/patientmr/charting/feed.dto';
import { INCIDENT_SEVERITIES } from '../../../../dtos/patientmr/charting/incident.dto';
import { codeLabel } from '../model/entryMeta';
import type { EntryErrors } from '../model/entryValidation';

export { codeLabel };


export const INCIDENT_TYPES = ['FALL', 'MEDICATION_ERROR', 'EQUIPMENT', 'INJURY', 'RESPIRATORY', 'SEIZURE', 'BEHAVIOR', 'OTHER'] as const;

interface FieldStyle {
  isMobile: boolean;
  disabled?: boolean;
}

const inputCls = ({ isMobile, disabled }: FieldStyle) =>
  `w-full px-2 ${isMobile ? 'text-base' : 'text-base md:text-sm'} border rounded border-gray-300 bg-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200 ${isMobile ? 'py-2.5 min-h-[44px]' : 'py-1'} ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`;

const Label: React.FC<{ htmlFor: string; className: string; children: React.ReactNode }> = ({ htmlFor, className, children }) => (
  <label htmlFor={htmlFor} className={`text-xs font-medium ${className}`}>{children}</label>
);

/** Structured pediatric feed: type, formula, volume, duration, tolerance, notes (Phase 9C) */
export const FeedFields: React.FC<FieldStyle & {
  value: FeedFormData;
  onChange: (next: FeedFormData) => void;
  firstRef?: React.Ref<HTMLSelectElement>;
  errors?: EntryErrors;
}> = ({ value, onChange, firstRef, isMobile, disabled, errors = {} }) => {
  const set = (field: keyof FeedFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    onChange({ ...value, [field]: e.target.value });
  const cls = inputCls({ isMobile, disabled });
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label htmlFor="feed-type" className="text-amber-700">Feed type<span className="text-red-600" aria-hidden="true"> *</span></Label>
          <select id="feed-type" ref={firstRef} disabled={disabled} className={cls} value={value.feedType} onChange={set('feedType')}>
            <option value="">Select…</option>
            {FEED_TYPES.map((t) => <option key={t} value={t}>{codeLabel(t)}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="feed-tolerance" className="text-amber-700">Tolerance</Label>
          <select id="feed-tolerance" disabled={disabled} className={cls} value={value.tolerance} onChange={set('tolerance')}>
            <option value="">Select…</option>
            {FEED_TOLERANCES.map((t) => <option key={t} value={t}>{codeLabel(t)}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="feed-volume" className="text-amber-700">Volume (mL)</Label>
          <input id="feed-volume" type="number" inputMode="decimal" min={0} max={5000} disabled={disabled} className={cls}
            value={value.volumeMl} onChange={set('volumeMl')} />
          {errors.volumeMl && <p className="text-xs text-red-600" data-testid="error-feed-volumeMl">{errors.volumeMl}</p>}
        </div>
        <div>
          <Label htmlFor="feed-duration" className="text-amber-700">Duration (min)</Label>
          <input id="feed-duration" type="number" inputMode="numeric" min={0} max={5000} disabled={disabled} className={cls}
            value={value.durationMinutes} onChange={set('durationMinutes')} />
          {errors.durationMinutes && <p className="text-xs text-red-600" data-testid="error-feed-durationMinutes">{errors.durationMinutes}</p>}
        </div>
        <div>
          <Label htmlFor="feed-rate" className="text-amber-700">Rate (mL/h)</Label>
          <input id="feed-rate" type="number" inputMode="decimal" min={0} max={1000} disabled={disabled} className={cls}
            value={value.rateMlPerHour} onChange={set('rateMlPerHour')} />
          {errors.rateMlPerHour && <p className="text-xs text-red-600" data-testid="error-feed-rateMlPerHour">{errors.rateMlPerHour}</p>}
        </div>
        <div>
          <Label htmlFor="feed-residual" className="text-amber-700">Residual (mL)</Label>
          <input id="feed-residual" type="number" inputMode="decimal" min={0} max={1000} disabled={disabled} className={cls}
            value={value.residualMl} onChange={set('residualMl')} />
          {errors.residualMl && <p className="text-xs text-red-600" data-testid="error-feed-residualMl">{errors.residualMl}</p>}
        </div>
      </div>
      <div>
        <Label htmlFor="feed-formula" className="text-amber-700">Formula / breast milk</Label>
        <input id="feed-formula" type="text" disabled={disabled} className={cls} value={value.formula} onChange={set('formula')} />
      </div>
      <div>
        <Label htmlFor="feed-notes" className="text-amber-700">Notes</Label>
        <textarea id="feed-notes" disabled={disabled} className={`${cls} resize-none`} rows={isMobile ? 4 : 3}
          value={value.notes} onChange={set('notes')} placeholder="Residual, position, response..." />
      </div>
    </div>
  );
};

/** Incident type and severity (CHT-08); description and internal-only stay with the caller */
export const IncidentHeaderFields: React.FC<FieldStyle & {
  value: IncidentFormData;
  onChange: (next: IncidentFormData) => void;
}> = ({ value, onChange, isMobile, disabled }) => {
  const cls = inputCls({ isMobile, disabled });
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label htmlFor="incident-type" className="text-red-700">Incident type<span className="text-red-600" aria-hidden="true"> *</span></Label>
        <select id="incident-type" disabled={disabled} className={cls} value={value.incidentType}
          onChange={(e) => onChange({ ...value, incidentType: e.target.value })}>
          <option value="">Select…</option>
          {INCIDENT_TYPES.map((t) => <option key={t} value={t}>{codeLabel(t)}</option>)}
        </select>
      </div>
      <div>
        <Label htmlFor="incident-severity" className="text-red-700">Severity</Label>
        <select id="incident-severity" disabled={disabled} className={cls} value={value.severity}
          onChange={(e) => onChange({ ...value, severity: e.target.value })}>
          <option value="">Select…</option>
          {INCIDENT_SEVERITIES.map((t) => <option key={t} value={t}>{codeLabel(t)}</option>)}
        </select>
      </div>
    </div>
  );
};

/** True when the feed form has something worth saving */
export const feedHasContent = (f: FeedFormData): boolean =>
  Boolean(f.feedType || f.tolerance || f.formula.trim() || f.volumeMl.trim() || f.durationMinutes.trim() || f.notes.trim()
    || (f.rateMlPerHour || '').trim() || (f.residualMl || '').trim());
